import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, BadgePercent, Check, CircleMinus, Headphones, Lock, RefreshCw, Send, ShieldCheck } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { Btn } from "@/components/site/Btn";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLang } from "@/lib/lang";
import { useDemoModal } from "@/components/site/DemoModal";

const TRUST_ICONS = [ShieldCheck, RefreshCw, Headphones, Lock];

export default function Tarifs() {
  const { t } = useTranslation();
  const lang = useLang();
  const openDemo = useDemoModal();
  const [billing, setBilling] = useState("annual");
  const plans = t("tarifs.plans", { returnObjects: true });
  const trust = t("tarifs.trust", { returnObjects: true });
  const faq = t("tarifs.faq", { returnObjects: true });

  return (
    <>
      <Seo title={t("tarifs.seo_title")} description={t("tarifs.seo_desc")} lang={lang} />
      <PageHero overline={t("tarifs.overline")} title={t("tarifs.title")} sub={t("tarifs.sub")} />

      <section className="px-6 pb-24 lg:px-10" data-testid="pricing-grid">
        <div className="mx-auto max-w-7xl">
          {/* Switch Annuel / Mensuel */}
          <Reveal className="flex items-center justify-center gap-3.5">
            <button
              type="button"
              role="switch"
              aria-checked={billing === "annual"}
              onClick={() => setBilling(billing === "annual" ? "monthly" : "annual")}
              data-testid="billing-switch"
              className="relative h-9 w-[68px] shrink-0 rounded-full bg-ostelo-deep"
            >
              <span
                className={`absolute top-1 h-7 w-7 rounded-full bg-white shadow transition-[left] duration-300 ${billing === "annual" ? "left-1" : "left-10"}`}
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              onClick={() => setBilling("annual")}
              data-testid="billing-annual-label"
              className={`text-sm transition-colors duration-300 ${billing === "annual" ? "font-bold text-ostelo-deep" : "font-medium text-ostelo-ink/40 hover:text-ostelo-ink/70"}`}
            >
              {t("tarifs.billing_annual")}
            </button>
            <button
              type="button"
              onClick={() => setBilling("monthly")}
              data-testid="billing-monthly-label"
              className={`text-sm transition-colors duration-300 ${billing === "monthly" ? "font-bold text-ostelo-deep" : "font-medium text-ostelo-ink/40 hover:text-ostelo-ink/70"}`}
            >
              {t("tarifs.billing_monthly")}
            </button>
          </Reveal>

          {/* Garanties */}
          <Reveal delay={0.08} className="mt-12 grid gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
            {trust.map((item, i) => {
              const Icon = TRUST_ICONS[i];
              return (
                <div key={item.title} className="flex items-start gap-3.5" data-testid={`trust-${i}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ostelo-sky text-ostelo-blue">
                    <Icon size={20} strokeWidth={1.9} aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-ostelo-deep">{item.title}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-ostelo-ink/55">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </Reveal>

          {/* Grille des 3 packs */}
          <div className="mt-16 grid gap-6 lg:grid-cols-3">
            {plans.map((p, i) => {
              const popular = p.key === "premium";
              const annual = billing === "annual";
              const isFree = p.annual_price === "0";
              const badgeCls =
                p.key === "essentiel"
                  ? "bg-ostelo-green/15 text-ostelo-green"
                  : p.key === "premium"
                    ? "bg-ostelo-orange text-ostelo-deep"
                    : "bg-ostelo-green text-white";
              const badgeText =
                p.key === "essentiel"
                  ? t("tarifs.badge_essentiel")
                  : annual
                    ? p.key === "excellence"
                      ? t("tarifs.badge_save")
                      : t("tarifs.badge_commitment_annual")
                    : t("tarifs.badge_no_commitment");
              return (
                <Reveal key={p.key} delay={i * 0.1}>
                  <div
                    data-testid={`plan-${p.key}`}
                    className={`relative flex h-full flex-col rounded-3xl border p-9 text-center transition-[box-shadow,transform] duration-500 hover:-translate-y-1.5 ${
                      popular
                        ? "border-ostelo-deep bg-ostelo-deep text-white shadow-[0_30px_70px_-20px_rgb(23,63,95,0.4)]"
                        : "border-ostelo-deep/10 bg-white hover:shadow-[0_20px_50px_rgb(23,63,95,0.08)]"
                    }`}
                  >
                    {popular && (
                      <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-ostelo-orange px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-ostelo-deep">
                        {t("tarifs.popular")}
                      </span>
                    )}
                    <p className={`font-display text-lg font-bold ${p.key === "excellence" ? "text-ostelo-violet" : popular ? "text-white" : "text-ostelo-deep"}`}>{p.name}</p>

                    {isFree ? (
                      <p className="mt-4 font-display text-2xl font-extrabold text-ostelo-green" data-testid={`plan-price-${p.key}`}>
                        {t("tarifs.free")}
                      </p>
                    ) : (
                      <p className="mt-4 flex flex-wrap items-baseline justify-center gap-x-2" data-testid={`plan-price-${p.key}`}>
                        {annual && (
                          <span className={`text-lg font-medium line-through ${popular ? "text-white/40" : "text-ostelo-ink/35"}`}>{p.annual_old}</span>
                        )}
                        <span className="font-display text-4xl font-extrabold tracking-tight text-ostelo-violet">{annual ? p.annual_price : p.monthly_price}</span>
                        <span className={`text-base font-bold ${popular ? "text-ostelo-orange" : "text-ostelo-ink/45"}`}>
                          {annual ? t("tarifs.per_year") : t("tarifs.per_month")}
                        </span>
                      </p>
                    )}

                    <span className={`mx-auto mt-4 inline-block rounded-full px-4 py-1.5 text-[11px] font-bold ${badgeCls}`} data-testid={`plan-badge-${p.key}`}>
                      {badgeText}
                    </span>

                    <p className={`mt-5 text-sm leading-relaxed ${popular ? "text-white/65" : "text-ostelo-ink/55"}`}>{p.desc}</p>

                    <ul className={`mt-7 flex-1 space-y-3 border-t pt-7 text-left ${popular ? "border-white/10" : "border-ostelo-deep/8"}`}>
                      {p.features.map((f) => (
                        <li key={f.text} className="flex items-center gap-3">
                          {f.off ? (
                            <CircleMinus size={16} className="shrink-0 text-ostelo-ink/25" aria-hidden="true" />
                          ) : f.violet ? (
                            <BadgePercent size={16} className="shrink-0 text-ostelo-violet" aria-hidden="true" />
                          ) : (
                            <Check size={16} strokeWidth={2.5} className="shrink-0 text-ostelo-green" aria-hidden="true" />
                          )}
                          <span className={`text-sm leading-relaxed ${f.off ? "text-ostelo-ink/35" : f.violet ? "font-semibold text-ostelo-violet" : popular ? "text-white/85" : "text-ostelo-ink/75"}`}>
                            {f.text}
                          </span>
                          {f.tag && (
                            <span className={`ml-auto shrink-0 rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider ${f.tag === "popular" ? "bg-ostelo-orange/15 text-ostelo-orange" : "bg-ostelo-violet/10 text-ostelo-violet"}`}>
                              {t(`tarifs.tag_${f.tag}`)}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-10">
                      <Btn variant={popular ? "accent" : "ghost"} testId={`plan-cta-${p.key}`} className="w-full justify-center" onClick={openDemo}>
                        {p.cta}
                      </Btn>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-ostelo-deep/8 bg-white px-6 py-24 lg:px-10" data-testid="pricing-faq">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-ostelo-deep sm:text-4xl">{t("tarifs.faq_title")}</h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <Accordion type="single" collapsible>
              {faq.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-display text-lg font-semibold text-ostelo-deep hover:text-ostelo-blue">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-ostelo-ink/65">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CTA final — bandeau compact */}
      <section className="px-6 pb-28 pt-4 lg:px-10" data-testid="tarifs-cta">
        <Reveal className="mx-auto max-w-7xl">
          <div className="flex flex-col items-start gap-8 rounded-[2.5rem] bg-ostelo-deep px-8 py-12 sm:px-12 lg:flex-row lg:items-center lg:gap-12 lg:px-16 lg:py-14">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white">
              <Send size={26} className="text-ostelo-blue" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                {t("tarifs.cta_title")}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-white/70 sm:text-base">
                {t("tarifs.cta_sub_1")}{" "}
                <span className="font-semibold text-ostelo-orange">{t("tarifs.cta_sub_accent")}</span>{" "}
                {t("tarifs.cta_sub_2")}
              </p>
            </div>
            <Btn variant="accent" testId="tarifs-cta-demo-button" className="shrink-0" onClick={openDemo}>
              {t("common.cta_demo")}
              <ArrowRight size={16} />
            </Btn>
          </div>
        </Reveal>
      </section>
    </>
  );
}
